import {Component, Inject, OnInit} from '@angular/core';
import { FormBuilder, FormControl, Validators, FormsModule, ReactiveFormsModule, FormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {PortalHotToastService, setupUntilDestroy} from '@portal-workspace/grow-ui-library';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {Application, CreditApprovedDialogData, CreditApprovedDialogResult, CreditReference, DealStatus, GetApplicationDscrFn, GetLvrCalculatorValueFn, ProductType, ValuationSource, creditReferenceOptions, dealStatusOptions, valuationSourceOptions, productTypeOptions, getAssetTier, isTransportIndustry, FULL_COMPLIANCE_STATUSES, EXCEPTION_STATUSES, ASSET_FINANCE_PRODUCT_OPTIONS, getPropertyOwner} from '@portal-workspace/grow-shared-library';
import { MatButtonModule } from '@angular/material/button';
import { FlexModule } from '@angular/flex-layout/flex';
import { MatOptionModule } from '@angular/material/core';
import moment from 'moment';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { CurrencyInputComponent } from '../currency-selection-component/currency-input.component';
import { NgIf } from '@angular/common';

@UntilDestroy()
@Component({
    templateUrl: './credit-approved.dialog.html',
    styleUrls: ['./credit-approved.dialog.scss'],
    standalone: true,
    imports: [MatFormFieldModule, MatSelectModule, FormsModule, CurrencyInputComponent, MatInputModule, ReactiveFormsModule, MatOptionModule, FlexModule, MatButtonModule, MatDialogModule, NgIf]
})
export class CreditApprovedDialog implements OnInit {

  formControlExceptionToPolicy!: FormControl<'Yes' | 'No' | null>;
  formControlCreditReference!: FormControl<CreditReference | null>;
  formControlDealStatus!: FormControl<DealStatus | null>;
  formControlFinancials!: FormControl<'Yes' | 'No' | null>;
  formControlValuationSource!: FormControl<ValuationSource | null>;
  formControlBankStatements!: FormControl<'Yes' | 'No' | null>;
  formControlProduct!: FormControl<ProductType | null>
  formControlApprovalConditions!: FormControl<string | null>;
  formControlLvr!: FormControl<number | null>;
  formControlDscr!: FormControl<number | null>;
  creditReferenceOptions = creditReferenceOptions;
  dealStatusOptions = dealStatusOptions;
  valuationSourceOptions = valuationSourceOptions;
  productTypeOptions = productTypeOptions;
  availableProductOptions: ProductType[] = productTypeOptions as ProductType[];
  yesNoOptions = ['No', 'Yes'];
  application!: Application;
  getApplicationDscrFn!: GetApplicationDscrFn;
  getLvrCalculatorValueFn?: GetLvrCalculatorValueFn;
  
  // Alert messages
  financialsError: string | null = null;
  bankStatementsError: string | null = null;
  lvrError: string | null = null;
  lvrWarning: string | null = null;
  dscrError: string | null = null;
  dscrWarning: string | null = null;
  

  formGroup!: FormGroup<{
    exceptionToPolicy: FormControl<'Yes' | 'No' | null>;
    creditReference: FormControl<CreditReference | null>;
    dealStatus: FormControl<DealStatus | null>;
    financials: FormControl<'Yes' | 'No' | null>;
    valuationSource: FormControl<ValuationSource | null>;
    bankStatements: FormControl<'Yes' | 'No' | null>;
    productType: FormControl<ProductType | null>;
    lvr: FormControl<number | null>;
    dscr: FormControl<number | null>;
    approvalConditions: FormControl<string | null>;
  }>;

  constructor(
    private formBuilder: FormBuilder,
    @Inject(MAT_DIALOG_DATA) data: CreditApprovedDialogData,
    private dialogRef: MatDialogRef<CreditApprovedDialog, CreditApprovedDialogResult>,
    private toastService: PortalHotToastService,
  ) {
    this.formControlExceptionToPolicy = this.formBuilder.control(null, [Validators.required]);
    this.formControlCreditReference = this.formBuilder.control(null, [Validators.required]);
    this.formControlDealStatus = this.formBuilder.control(null, [Validators.required]);
    this.formControlFinancials = this.formBuilder.control(null, [Validators.required]);
    this.formControlValuationSource = this.formBuilder.control(null, [Validators.required]);
    this.formControlBankStatements = this.formBuilder.control(null, [Validators.required]);
    this.formControlProduct = this.formBuilder.control(null, [Validators.required]);
    this.formControlLvr = this.formBuilder.control<number | null>(null);
    this.formControlDscr = this.formBuilder.control(0, [Validators.required])
    this.formControlApprovalConditions = this.formBuilder.control(null);

    this.formGroup = formBuilder.group({
      exceptionToPolicy: this.formControlExceptionToPolicy,
      creditReference: this.formControlCreditReference,
      dealStatus: this.formControlDealStatus,
      financials: this.formControlFinancials,
      valuationSource: this.formControlValuationSource,
      bankStatements: this.formControlBankStatements,
      productType: this.formControlProduct,
      lvr: this.formControlLvr,
      dscr: this.formControlDscr,
      approvalConditions: this.formControlApprovalConditions,
    })

    this.application = data.application;
    this.getApplicationDscrFn = data.getApplicationDscrFn;
    this.getLvrCalculatorValueFn = data.getLvrCalculatorValueFn;
  }

  ngOnInit(): void {
    setupUntilDestroy(this);

    // Set up product restrictions based on ApplicationType
    this.setupProductRestrictions();
    this.syncLvrControlState();

    // Pre-fill LVR from application
    this.prefillLvr();

    // Pre-fill Product and DSCR based on product type
    this.prefillProductAndDscr();

    // Set up value change subscriptions for validation and pre-fill logic
    this.setupValueChangeSubscriptions();

    // Pre-fill Financials and Bank Statements based on initial product value
    // (This needs to happen after setupValueChangeSubscriptions since the initial
    // product setValue in setupProductRestrictions doesn't trigger valueChanges)
    this.prefillFinancialsAndBankStatementsOnInit();

    // Initial validation
    this.validateAll();
  }

  private syncLvrControlState(): void {
    if (this.isBusinessLoanOrOverdraft()) {
      this.formControlLvr.clearValidators();
      this.formControlLvr.disable({ emitEvent: false });
      this.formControlLvr.updateValueAndValidity({ emitEvent: false });
      return;
    }
    this.formControlLvr.setValidators([Validators.required]);
    this.formControlLvr.enable({ emitEvent: false });
    this.formControlLvr.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Set up product dropdown restrictions based on ApplicationType
   */
  private setupProductRestrictions(): void {
    const appType = this.application?.ApplicationType;

    if (appType === 'AssetFinance') {
      // Asset Finance: restrict to only FAST DOC, EASY DOC, FULL DOC
      this.availableProductOptions = ASSET_FINANCE_PRODUCT_OPTIONS;
    } else if (appType === 'BusinessLoans' || appType === 'BusinessOverdraft') {
      // Business Loan / Business Overdraft: pre-fill BUSINESS LOAN
      this.availableProductOptions = productTypeOptions as ProductType[];
      this.formControlProduct.setValue('BUSINESS LOAN');
    } else {
      // Other record types: show all options, do not pre-fill
      this.availableProductOptions = productTypeOptions as ProductType[];
    }
  }

  /**
   * Pre-fill LVR from application AssetSpec or Property details
   */
  private prefillLvr(): void {
    if (this.application) {
      // For Asset Finance, use AssetSpec.LVR
      if (this.application.ApplicationType === 'AssetFinance' && this.application?.AssetSpec?.LVR) {
        const lvr = isNaN(Number(this.application?.AssetSpec?.LVR ?? 0)) ? null : Number(this.application?.AssetSpec?.LVR);
        this.formControlLvr.setValue(lvr);
      }
      // For Business Loans/Overdraft, fetch latest LvrCalculatorValue from API
      if ((this.application.ApplicationType === 'BusinessLoans' || this.application.ApplicationType === 'BusinessOverdraft') 
          && this.getLvrCalculatorValueFn) {
        this.getLvrCalculatorValueFn(this.application.ApplicationId).pipe(
          this.toastService.spinnerObservable()
        ).subscribe(lvrCalculatorValue => {
          const proposedLvr = lvrCalculatorValue?.proposedLvrPercent;
          const lvr = (proposedLvr == null || isNaN(Number(proposedLvr))) ? null : Number(proposedLvr);
          this.formControlLvr.setValue(lvr);
          this.validateLvr();
        });
      }
    }
  }

  /**
   * Pre-fill Product and DSCR based on application product type
   */
  private prefillProductAndDscr(): void {
    const appType = this.application?.ApplicationType;
    const appProductType = (this.application?.AppInfo as any)?.productType;

    // Pre-fill Product for Asset Finance based on existing productType
    if (appType === 'AssetFinance' && appProductType) {
      const mappedProduct = this.mapAppProductTypeToProductType(appProductType);
      if (mappedProduct && ASSET_FINANCE_PRODUCT_OPTIONS.includes(mappedProduct)) {
        this.formControlProduct.setValue(mappedProduct);
      }
    }

    // Pre-fill DSCR
    if (appType === 'AssetFinance' && (appProductType === 'FAST DOC' || appProductType === 'LOW DOC')) {
      // Pre-fill 9999 if Product = FAST DOC for Asset Finance only
      this.formControlDscr.setValue(9999);
    } else {
      // Populate DSCR from bank statements calculator
      if (this.application && (
        appType === 'AssetFinance' || 
        appType === 'BusinessLoans' ||
        appType === 'BusinessOverdraft')
      ) {
        this.getApplicationDscrFn(this.application.ApplicationId).pipe(
          this.toastService.spinnerObservable()
        ).subscribe(dscr => {
          if (dscr) {
            this.formControlDscr.setValue(dscr)
          }
          // Leave blank (0) for manual entry if no DSCR returned
        })
      }
    }
  }

  /**
   * Map application productType to ProductType enum
   */
  private mapAppProductTypeToProductType(appProductType: string): ProductType | null {
    switch (appProductType?.toUpperCase()) {
      case 'FAST DOC':
      case 'LOW DOC':
        return 'FAST DOC';
      case 'EASY DOC':
        return 'EASY DOC';
      case 'FULL DOC':
        return 'FULL DOC';
      default:
        return null;
    }
  }

  /**
   * Set up subscriptions for value changes to trigger pre-fill and validation
   */
  private setupValueChangeSubscriptions(): void {
    // Deal Status changes - pre-fill Exception to Policy
    this.formControlDealStatus.valueChanges.pipe(
      untilDestroyed(this)
    ).subscribe(dealStatus => {
      if (dealStatus) {
        this.prefillExceptionToPolicy(dealStatus);
      }
      this.validateAll();
    });

    // Product changes - pre-fill Financials and Bank Statements, update DSCR
    this.formControlProduct.valueChanges.pipe(
      untilDestroyed(this)
    ).subscribe(product => {
      if (product) {
        this.prefillFinancialsAndBankStatements(product);
        this.handleProductDscrPrefill(product);
      }
      this.validateAll();
    });

    // Exception to Policy changes - revalidate LVR and DSCR
    this.formControlExceptionToPolicy.valueChanges.pipe(
      untilDestroyed(this)
    ).subscribe(() => {
      this.validateAll();
    });

    // Financials changes - validate
    this.formControlFinancials.valueChanges.pipe(
      untilDestroyed(this)
    ).subscribe(() => {
      this.validateFinancials();
    });

    // Bank Statements changes - validate
    this.formControlBankStatements.valueChanges.pipe(
      untilDestroyed(this)
    ).subscribe(() => {
      this.validateBankStatements();
    });

    // LVR changes - validate
    this.formControlLvr.valueChanges.pipe(
      untilDestroyed(this)
    ).subscribe(() => {
      this.validateLvr();
    });

    // DSCR changes - validate
    this.formControlDscr.valueChanges.pipe(
      untilDestroyed(this)
    ).subscribe(() => {
      this.validateDscr();
    });
  }

  /**
   * Pre-fill Exception to Policy based on Deal Status
   * YES if Deal Status 4-8 (Exception), NO if Deal Status 1-3 (Full Compliance)
   */
  private prefillExceptionToPolicy(dealStatus: DealStatus): void {
    if (FULL_COMPLIANCE_STATUSES.includes(dealStatus)) {
      this.formControlExceptionToPolicy.setValue('No');
    } else if (EXCEPTION_STATUSES.includes(dealStatus)) {
      this.formControlExceptionToPolicy.setValue('Yes');
    }
  }

  /**
   * Pre-fill Financials and Bank Statements based on Product
   */
  private prefillFinancialsAndBankStatements(product: ProductType): void {
    // Financials: YES if Product = FULL DOC, NO otherwise
    if (product === 'FULL DOC') {
      this.formControlFinancials.setValue('Yes');
    } else {
      this.formControlFinancials.setValue('No');
    }

    // Bank Statements: YES if Product = EASY DOC or BUSINESS LOAN, NO otherwise
    if (product === 'EASY DOC' || product === 'BUSINESS LOAN') {
      this.formControlBankStatements.setValue('Yes');
    } else {
      this.formControlBankStatements.setValue('No');
    }
  }

  /**
   * Pre-fill Financials and Bank Statements on init based on:
   * 1. Product type (existing logic)
   * 2. Application's bank statement submitted status (if available)
   */
  private prefillFinancialsAndBankStatementsOnInit(): void {
    const product = this.formControlProduct.value;
    const appInfo = this.application?.AppInfo as any;
    
    // Pre-fill Financials based on product
    if (product === 'FULL DOC') {
      this.formControlFinancials.setValue('Yes');
    } else if (product) {
      this.formControlFinancials.setValue('No');
    }

    // Pre-fill Bank Statements
    // First check if application has BankStatementSubmitted = 'Yes'
    if (appInfo?.BankStatementSubmitted === 'Yes') {
      this.formControlBankStatements.setValue('Yes');
    } else if (product === 'EASY DOC' || product === 'BUSINESS LOAN') {
      // Fallback to product-based logic
      this.formControlBankStatements.setValue('Yes');
    } else if (product) {
      this.formControlBankStatements.setValue('No');
    }
  }

  /**
   * Handle DSCR pre-fill when product changes
   */
  private handleProductDscrPrefill(product: ProductType): void {
    if (product === 'FAST DOC') {
      this.formControlDscr.setValue(9999);
    } else if (this.formControlDscr.value === 9999) {
      // If switching away from FAST DOC and DSCR is 9999, clear it
      this.formControlDscr.setValue(0);
    }
  }

  /**
   * Validate all fields
   */
  private validateAll(): void {
    this.validateFinancials();
    this.validateBankStatements();
    this.validateLvr();
    this.validateDscr();
  }

  /**
   * Validate Financials field
   * Error if = NO & Product = FULL DOC
   */
  private validateFinancials(): void {
    const financials = this.formControlFinancials.value;
    const product = this.formControlProduct.value;

    if (financials === 'No' && product === 'FULL DOC') {
      this.financialsError = 'Financials are required for Full Doc products';
    } else {
      this.financialsError = null;
    }
  }

  /**
   * Validate Bank Statements field
   * Error if = NO & Product = EASY DOC or BUSINESS LOAN
   */
  private validateBankStatements(): void {
    const bankStatements = this.formControlBankStatements.value;
    const product = this.formControlProduct.value;

    if (bankStatements === 'No' && (product === 'EASY DOC' || product === 'BUSINESS LOAN')) {
      this.bankStatementsError = 'Bank statements are required for Easy Doc and Business Loan products';
    } else {
      this.bankStatementsError = null;
    }
  }

  /**
   * Validate LVR field
   * Error alerts:
   *   - >125% & Tier 1/2 & Exception=NO
   *   - >110% & Tier 3/4 & Exception=NO
   *   - >100% & Business Loan & Exception=NO
   * Advisory alerts:
   *   - <10% & not blank/zero
   *   - >100% & not blocked
   */
  private validateLvr(): void {
    const lvr = this.formControlLvr.value;
    const isException = this.formControlExceptionToPolicy.value === 'Yes';
    const product = this.formControlProduct.value;
    const appType = this.application?.ApplicationType;
    const propertyOwner = (getPropertyOwner(this.application) ?? '').toLowerCase() === 'yes';

    // Reset alerts
    this.lvrError = null;
    this.lvrWarning = null;

    // BL/OD: Property owners require a non-blank system LVR.
    if ((appType === 'BusinessLoans' || appType === 'BusinessOverdraft') && propertyOwner && lvr == null) {
      this.lvrError = 'Error: Invalid LVR for Property Owner';
      return;
    }

    // Skip validation if LVR is blank or zero
    if (lvr == null || lvr === 0) {
      return;
    }

    // Get asset tier for Asset Finance applications
    let tier: number | null = null;
    if (appType === 'AssetFinance' && this.application?.AppInfo) {
      const assetCategoryIndex = (this.application.AppInfo as any)?.AssetCategory;
      if (assetCategoryIndex) {
        tier = getAssetTier(assetCategoryIndex);
      }
    }

    // Error validations (blocking)
    if (!isException) {
      // >125% & Tier 1/2 & not policy exception
      if (lvr > 125 && (tier === 1 || tier === 2)) {
        this.lvrError = 'LVR exceeds 125% limit for Tier 1/2 assets. Policy exception required.';
        return;
      }

      // >110% & Tier 3/4 & not policy exception
      if (lvr > 110 && (tier === 3 || tier === 4)) {
        this.lvrError = 'LVR exceeds 110% limit for Tier 3/4 assets. Policy exception required.';
        return;
      }

      // >100% & Business Loan & not policy exception
      if (lvr > 100 && (product === 'BUSINESS LOAN' || appType === 'BusinessLoans' || appType === 'BusinessOverdraft')) {
        this.lvrError = 'LVR exceeds 100% limit for Business Loans. Policy exception required.';
        return;
      }
    }

    // Advisory validations (warning, can proceed)
    // <10% (to capture LVR/DSCR transposition errors)
    if (lvr > 0 && lvr < 10) {
      this.lvrWarning = 'LVR < 10%.';
      return;
    }

    // >100% (please verify calculation)
    if (lvr > 100) {
      this.lvrWarning = 'LVR > 100%.';
    }
  }

  /**
   * Validate DSCR field
   * Error alerts:
   *   - <1.10x & not policy exception
   *   - <1.25x & not transport ANZSIC & not policy exception
   *   - =9999 & product != FAST DOC
   * Advisory alerts:
   *   - >10x & != 9999 (to capture LVR/DSCR transposition errors)
   */
  private validateDscr(): void {
    const dscr = this.formControlDscr.value ?? 0;
    const isException = this.formControlExceptionToPolicy.value === 'Yes';
    const product = this.formControlProduct.value;

    // Reset alerts
    this.dscrError = null;
    this.dscrWarning = null;

    // Error: DSCR = 9999 & Product != FAST DOC
    if (dscr === 9999 && product !== 'FAST DOC') {
      this.dscrError = 'DSCR value 9999 is only valid for Fast Doc products.';
      return;
    }

    // Skip other validations for FAST DOC with 9999
    if (dscr === 9999 && product === 'FAST DOC') {
      return;
    }

    // Skip validation if DSCR is 0 (not entered)
    if (!dscr || dscr === 0) {
      return;
    }

    // Error validations (blocking) - only if not policy exception
    if (!isException) {
      // <1.10x & not policy exception
      if (dscr < 1.10) {
        this.dscrError = 'DSCR is below 1.10x minimum. Policy exception required.';
        return;
      }

      // <1.25x & not transport ANZSIC & not policy exception
      if (dscr < 1.25 && !isTransportIndustry(this.application)) {
        this.dscrError = 'DSCR is below 1.25x for non-transport industry. Policy exception required.';
        return;
      }
    }

    // Advisory validation (warning, can proceed)
    // >10x & != 9999 (to capture LVR/DSCR transposition errors)
    if (dscr > 10 && dscr !== 9999) {
        this.dscrWarning = 'DSCR > 10x.';
      }
  }

  /**
   * Check if there are any blocking errors
   */
  hasBlockingErrors(): boolean {
    return !!(this.financialsError || this.bankStatementsError || this.lvrError || this.dscrError);
  }

  /**
   * Check if form can be submitted
   */
  canSubmit(): boolean {
    return this.formGroup.valid && !this.hasBlockingErrors();
  }

  isBusinessLoanOrOverdraft(): boolean {
    return this.application?.ApplicationType === 'BusinessLoans' || this.application?.ApplicationType === 'BusinessOverdraft';
  }

  onSubmit(readyForSubmission: boolean) {
    if (readyForSubmission) {
      this.submitApproval();
    } else {
      this.dialogRef.close();
    }
  }

  private submitApproval() {
    this.dialogRef.close({
      creditApprovalDate: moment().format('YYYY-MM-DD'),
      exceptionToPolicy: this.formControlExceptionToPolicy.value as 'Yes' | 'No',
      creditReference: this.formControlCreditReference.value as CreditReference,
      dealStatus: this.formControlDealStatus.value as DealStatus,
      financials: this.formControlFinancials.value as 'Yes' | 'No',
      valuationSource: this.formControlValuationSource.value as ValuationSource,
      bankStatements: this.formControlBankStatements.value as 'Yes' | 'No',
      productType: this.formControlProduct.value as ProductType,
      approvalConditions: this.formControlApprovalConditions.value ?? '',
      lvr: this.formControlLvr.value ?? null,
      dscr: this.formControlDscr.value ?? 0,
    });
  }
}
